import { useState, useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';
import { getCachedClients, cacheClients } from '@/lib/offlineDataCache';
import { ClientSelector } from '@/components/quotes/ClientSelector';
import { CatalogSelector } from '@/components/quotes/CatalogSelector';
import { SortableQuoteItemCard, QuoteItem } from '@/components/quotes/SortableQuoteItemCard';
import { StatusBadge, QuoteStatusType } from '@/components/quotes/StatusBadge';
import type { Client, PaymentMethod, PdfTemplateName, Product } from '@/types/database';
import { PdfTemplatePicker } from '@/components/company/PdfTemplatePicker';
import { PDF_TEMPLATE_LABELS } from '@/components/company/PdfTemplatePreview';
import { resolveQuotePdfTemplate } from '@/lib/quotePdfTemplate';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useFeature } from '@/hooks/useFeature';
import { parentIdsWithVariants } from '@/lib/variants';
import { computeQuoteTotals, quoteGrandTotal, type QuoteTotalsInput } from '@/lib/quoteTotals';
import { QuoteTotalsBreakdown } from '@/components/quotes/QuoteTotalsBreakdown';
import { MoneyInput } from '@/components/ui/money-input';
import { DiscountField } from '@/components/quotes/DiscountField';
import { QUOTE_PAYMENT_METHODS, normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import {
  WARRANTY_UNITS,
  formatStructuredWarranty,
  suggestWarranty,
  type WarrantyUnit,
} from '@/lib/quoteWarranty';
import { isBillableQuoteLine } from '@/lib/billableExpense';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import { 
  Calendar,
  User,
  Package,
  Pencil,
  FileText,
  Receipt,
  ClipboardList,
  FileCheck,
  Phone,
  MapPin,
  UserPlus,
  X,
  Hash,
  FileSpreadsheet,
  FilePen,
  CheckSquare,
  Contact,
  ScanLine,
  Barcode,
  Plus,
  Trash2,
  Wrench,
  ShoppingBag,
  Search,
  RefreshCw,
  Loader2,
  CreditCard,
  MessageSquareText,
  Palette,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { FieldLabel, FIELD_INPUT } from '@/components/ui/field';
import {
  MAX_EXTRA_FIELDS,
  MAX_EXTRA_LABEL,
  MAX_EXTRA_VALUE,
  type QuoteExtraField,
} from '@/lib/quoteExtraFields';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { GeneralPhotosSection, GeneralPhoto } from '@/components/quotes/GeneralPhotosSection';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import {
  deletePendingUploads,
  splitPendingUploads,
  trackPendingUpload,
  type PendingUpload,
} from '@/lib/pendingUploads';
import { PremiumModal } from '@/components/modals/PremiumModal';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { isEan13 } from '@/lib/barcodeUtils';
import {
  detectDocumentKind,
  formatCep,
  formatDocument,
  isValidDocument,
  onlyDigits,
} from '@/lib/documentUtils';
import { lookupCep } from '@/lib/cepLookup';
import { cacheBundles, getCachedBundles, getCachedCatalog } from '@/lib/offlineDataCache';
import { createUuid } from '@/lib/uuid';
import { productUnitCost } from '@/lib/cost';
import { todayIsoDate } from '@/lib/financeStatus';
import {
  bundleLinesForQuote,
  groupBundleItems,
  resolveBundleItems,
  type BundleItemRow,
} from '@/lib/productBundles';

export type DocumentType = 'orcamento' | 'pedido' | 'recibo' | 'ordem_servico' | 'proposta_comercial' | 'cotacao' | 'estimativa' | 'plano_trabalho' | 'comprovante';

export interface QuoteFormData {
  clientName: string;
  selectedClient: Client | null;
  quoteDate: string;
  status: QuoteStatusType;
  documentType: DocumentType;
  validityDays: string;
  /**
   * Garantia em TEXTO LIVRE — o campo histórico, que continua existindo.
   * Documento antigo guarda a frase inteira aqui ("1 ano na peça, 90 dias na
   * mão de obra"), e ela nunca é convertida sozinha (ver `src/lib/quoteWarranty.ts`).
   */
  warranty: string;
  /** Garantia ESTRUTURADA. `null` = não informada — nunca zero. */
  warrantyAmount: number | null;
  warrantyUnit: WarrantyUnit | null;
  notes: string;
  termsConditions: string;
  items: QuoteItem[];
  /**
   * Frete do documento (`quotes.shipping`). Somado por ÚLTIMO e **não** recebe
   * desconto — a ordem inteira está documentada em `src/lib/quoteTotals.ts`.
   */
  shipping?: number;
  /** Desconto do CABEÇALHO (`quotes.discount`), além dos descontos por item. */
  discount?: number;
  /** `value` | `percent` (`quotes.discount_type`). */
  discountType?: 'value' | 'percent';
  /**
   * Campos de EXIBIÇÃO (grupo 4) — nenhum entra no cálculo do total.
   *
   * `paymentMethods` distingue `null` (não informado/legado → o PDF mantém o QR
   * PIX) de `[]` (o usuário desmarcou tudo → esconde o QR). A regra inteira e o
   * porquê estão em `src/lib/quotePaymentInfo.ts`.
   */
  paymentMethods?: PaymentMethod[] | null;
  /** Condições de pagamento em texto livre (`quotes.payment_terms`). */
  paymentTerms?: string;
  /** Referência EXTERNA do cliente (`quotes.reference`) — não é o `sequenceId`. */
  reference?: string;
  /**
   * Modelo de PDF DESTE documento (`quotes.pdf_template`). `null` = segue o
   * padrão da empresa — o estado de todo orçamento antigo e de quem nunca abre
   * o seletor. Nunca converta ausência em 'classico' (ver quotePdfTemplate.ts).
   */
  pdfTemplate?: PdfTemplateName | null;
  /*
   * CADASTRO RÁPIDO DO CLIENTE — os mesmos campos de /clients/new.
   *
   * Eles são gravados de verdade quando "salvar na minha lista" está marcado: o
   * builder único `buildNewClientColumns` (src/lib/clientAddress.ts) monta as
   * colunas para os três caminhos que inserem este cliente (os dois saves online
   * da tela e a fila offline). `newClientAddress` continua existindo porque é a
   * linha ÚNICA que o documento e o PDF imprimem — os campos estruturados são o
   * que a NFS-e precisa, e a linha é composta deles quando ela está vazia.
   */
  newClientPhone?: string;
  newClientAddress?: string;
  newClientDocument?: string;
  newClientEmail?: string;
  newClientCep?: string;
  newClientLogradouro?: string;
  newClientNumero?: string;
  newClientComplemento?: string;
  newClientBairro?: string;
  newClientMunicipio?: string;
  newClientUf?: string;
  newClientIe?: string;
  saveNewClient?: boolean;
  // Title and sequential numbering
  title?: string;
  /**
   * Linha secundária do cabeçalho (`quotes.subtitle`). Existe porque o #202 fez
   * o TÍTULO substituir a palavra do tipo: com subtítulo dá para ter os dois.
   * `''` = ausente, e ausente mantém o PDF idêntico ao histórico.
   */
  subtitle?: string;
  /**
   * Cidade de emissão (`quotes.issue_place`) — a linha "São Paulo, 9 de agosto
   * de 2026". A DATA sozinha nunca é impressa: sem local, o documento sai como
   * sempre saiu. Regra inteira em `src/lib/quoteHeading.ts`.
   */
  issuePlace?: string;
  /**
   * Campos EXTRAS nomeados pelo prestador (`quotes.extra_fields`). Lista
   * vazia = documento sem campos extras, o estado de todo o histórico.
   * Regras (meio par é descartado, ordem é dado) em `src/lib/quoteExtraFields.ts`.
   */
  extraFields?: QuoteExtraField[];
  sequenceId?: number;
  showSequenceId?: boolean;
  // General photos
  generalPhotos?: GeneralPhoto[];
  /**
   * As fotos que ESTE formulário já subiu para o Storage e que ainda não têm
   * dono no banco (o upload é no ato da seleção, não no save).
   *
   * Vive DENTRO do `formData` de propósito: assim ela viaja no rascunho
   * autossalvo e sobrevive a sair da tela e voltar por "Continuar orçamento" —
   * que é justamente o caso em que apagar as fotos seria um desastre. Quem
   * apaga é o save (o que sobrou sem uso) ou o descarte explícito do rascunho.
   *
   * Item vindo do CATÁLOGO nunca entra aqui: a foto dele é do produto e
   * sobrevive ao documento. Ver `src/lib/pendingUploads.ts`.
   *
   * Campo de trabalho do formulário — nenhum payload de `quotes` o cita (os
   * payloads são montados por lista explícita de colunas, #62).
   */
  pendingUploads?: PendingUpload[];
}

// Re-export QuoteItem for backward compatibility
export type { QuoteItem } from '@/components/quotes/SortableQuoteItemCard';
export type { GeneralPhoto } from '@/components/quotes/GeneralPhotosSection';

interface QuoteFormProps {
  data: QuoteFormData;
  onChange: Dispatch<SetStateAction<QuoteFormData>>;
  disabled?: boolean;
  showSaveToCatalog?: boolean;
  showTypeAndUnit?: boolean;
  /** Called when user tries to change client on an already-saved quote */
  onClientChangeRequest?: (callback: () => void) => void;
  /** Whether this is an existing saved quote (has ID in database) */
  isExistingQuote?: boolean;
  /**
   * Numeração e status dentro do accordion "Identificação".
   *
   * A tela de CRIAÇÃO mostra os dois no cabeçalho verde (número, switch do PDF e
   * as pílulas de status), então lá isto vem `false` — senão os mesmos controles
   * apareceriam duas vezes. A tela de edição, que não tem esse cabeçalho, mantém
   * o padrão `true` e continua com todos os campos.
   */
  showIdentityFields?: boolean;
}

const FREE_PHOTO_LIMIT = 3;

/**
 * Sugestões de condição de pagamento — texto livre com atalhos, e não uma lista
 * de presets fechada.
 *
 * A decisão: `payment_terms` é texto livre (coerente com `warranty`, que já é
 * assim). Uma condição real varia demais para caber num enum — "50% na entrada
 * e 50% na entrega", "3x sem juros no cartão", "à vista com 5% de desconto",
 * "30/60/90 dias" — e presets fechados ou seriam grossos demais ou exigiriam
 * uma tela de edição e MAIS UMA TABELA. Os chips abaixo entregam o atalho dos
 * presets (um toque preenche o campo) sem nada disso, e o resultado continua
 * editável. Só um clique preenche: eles NÃO concatenam, senão dois toques
 * distraídos gerariam "À vistaAté 3x".
 */
const PAYMENT_TERMS_SUGGESTIONS = [
  'À vista',
  '50% na entrada e 50% na entrega',
  'Até 3x sem juros',
  '30 dias após a entrega',
];

/**
 * Campo de valor em R$ do Card Total (frete e desconto de cabeçalho).
 *
 * Estado de TEXTO local porque o usuário digita "12," no meio do caminho —
 * converter a cada tecla devolveria o campo reformatado embaixo do cursor.
 *
 * Usa `parseBRLInput`/`numberToBRLInput` (os helpers de DINHEIRO, já usados nos
 * Recibos) e NÃO os de custo: `parseCostInput` não conhece separador de milhar,
 * então "1.234,56" viraria R$ 1,23 — em silêncio, num campo cujo valor vai
 * direto para o total do documento.
 */
/**
 * PADRÃO DE CAMPO EM LINHA COMPARTILHADA — agora em `@/components/ui/field`,
 * porque o formulário de cliente adotou o mesmo desenho (ver o cabeçalho de lá).
 */

// Document type options grouped by category
const documentTypeGroups = [
  {
    label: 'Orçamentos e propostas',
    options: [
      { value: 'orcamento', label: 'Orçamento', icon: FileText },
      { value: 'proposta_comercial', label: 'Proposta comercial', icon: FilePen },
      { value: 'cotacao', label: 'Cotação', icon: FileSpreadsheet },
      { value: 'estimativa', label: 'Estimativa', icon: FileText },
    ],
  },
];

// Flatten for easy lookup
const allDocumentTypeOptions = documentTypeGroups.flatMap(g => g.options);

// Document type labels with proper gender prepositions (do/da) - short label and full preposition
const documentTypeLabels: Record<string, { short: string; preposition: string; itemsPreposition: string }> = {
  'orcamento': { short: 'Orçamento', preposition: 'do orçamento', itemsPreposition: 'do orçamento' },
  'proposta_comercial': { short: 'Proposta comercial', preposition: 'da proposta comercial', itemsPreposition: 'da proposta' },
  'cotacao': { short: 'Cotação', preposition: 'da cotação', itemsPreposition: 'da cotação' },
  'estimativa': { short: 'Estimativa', preposition: 'da estimativa', itemsPreposition: 'da estimativa' },
  'pedido': { short: 'Pedido de venda', preposition: 'do pedido de venda', itemsPreposition: 'do pedido' },
  'ordem_servico': { short: 'Ordem de serviço', preposition: 'da ordem de serviço', itemsPreposition: 'da O.S.' },
  'plano_trabalho': { short: 'Plano de trabalho', preposition: 'do plano de trabalho', itemsPreposition: 'do plano' },
  'recibo': { short: 'Recibo', preposition: 'do recibo', itemsPreposition: 'do recibo' },
  'comprovante': { short: 'Comprovante', preposition: 'do comprovante', itemsPreposition: 'do comprovante' },
};

// Helper function to get document type label info
const getDocumentTypeLabel = (docType: string) => {
  return documentTypeLabels[docType] || { short: 'Orçamento', preposition: 'do orçamento', itemsPreposition: 'do orçamento' };
};

export function QuoteForm({ 
  data, 
  onChange, 
  disabled = false, 
  showSaveToCatalog = true, 
  showTypeAndUnit = false,
  onClientChangeRequest,
  isExistingQuote = false,
  showIdentityFields = true,
}: QuoteFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isPremium } = useSubscription();
  const { isOnline } = useNetworkStatus();
  // Variações são PRO — o card precisa do portão para desenhar o selo e abrir a
  // paywall, e não pode chamar `useFeature` por item (um hook por linha da tela).
  const productVariants = useFeature('product_variants');

  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showCatalogSelector, setShowCatalogSelector] = useState(false);
  const [showProModal, setShowProModal] = useState(false);

  // Client list for the inline "ou digite:" autocomplete
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [clientInputFocused, setClientInputFocused] = useState(false);

  /**
   * Materiais inclusos (kits), para expandir um serviço-kit em linhas de
   * material quando ele é escolhido.
   *
   * Cache primeiro e servidor depois, como o resto do formulário: montar o
   * orçamento a partir de um kit tem que funcionar SEM rede — os materiais são
   * `quote_items` locais como qualquer item do catálogo, e o `product_id` deles
   * já é o id real do produto (o vínculo é online-only, então nunca é
   * `offline_`).
   */
  const [bundleRows, setBundleRows] = useState<BundleItemRow[]>([]);

  useEffect(() => {
    if (!user) return;

    const cachedBundles = getCachedBundles(user.id);
    if (cachedBundles) setBundleRows(cachedBundles as BundleItemRow[]);

    if (!isOnline || !activeCompany) return;

    let cancelled = false;
    supabase
      .from('product_bundle_items')
      .select('*')
      .eq('company_id', activeCompany.id)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        setBundleRows(data as BundleItemRow[]);
        cacheBundles(user.id, data);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, isOnline, activeCompany?.id]);

  const bundlesByParent = useMemo(() => groupBundleItems(bundleRows), [bundleRows]);

  /**
   * O que o card de item precisa para editar VARIAÇÕES e MATERIAIS do produto
   * que o "salvar este item no meu catálogo" vai criar. Montado aqui porque o
   * formulário já carrega catálogo e kits (cache primeiro, servidor depois) —
   * buscar de dentro do card daria uma consulta por item na tela.
   */
  const catalogProductsForCards = useMemo(
    () => (user ? ((getCachedCatalog(user.id) || []) as Product[]) : []),
    // `bundleRows` entra de propósito: ele muda depois da busca do servidor, e é
    // o gatilho mais barato para reler o catálogo já aquecido pelo warmup.
    [user?.id, bundleRows],
  );

  const catalogContext = useMemo(
    () => ({
      products: catalogProductsForCards,
      parentsWithVariants: parentIdsWithVariants(catalogProductsForCards),
      kitParentIds: new Set(bundleRows.map((row) => String(row.parent_product_id))),
      materialIds: new Set(bundleRows.map((row) => String(row.child_product_id))),
      isOnline,
      companyId: activeCompany?.id ?? null,
      variantsAllowed: productVariants.allowed,
      requireVariants: () => productVariants.require(),
    }),
    [catalogProductsForCards, bundleRows, isOnline, activeCompany?.id, productVariants],
  );

  // Load the user's clients (cache-first, then refresh from server) so the
  // "ou digite:" field can suggest existing clients as the user types.
  useEffect(() => {
    if (!user) return;

    const cached = getCachedClients(user.id);
    if (cached && cached.length > 0) {
      setAllClients(cached as Client[]);
    }

    if (isOnline && activeCompany) {
      supabase
        .from('clients')
        .select('*')
        .eq('company_id', activeCompany.id)
        .order('name')
        .then(({ data, error }) => {
          if (data && !error) {
            setAllClients(data as Client[]);
            cacheClients(user.id, data);
          }
        });
    }
  }, [user, isOnline, activeCompany?.id]);

  // Matches for the inline autocomplete dropdown: only while the user is typing
  // a name that doesn't already exactly match the selected client.
  const clientSuggestions = useMemo(() => {
    const query = data.clientName.trim().toLowerCase();
    if (!query) return [];
    // Hide the dropdown once an exact match is already selected.
    if (data.selectedClient && data.selectedClient.name.toLowerCase() === query) {
      return [];
    }
    return allClients
      .filter((c) => c.name.toLowerCase().includes(query))
      .slice(0, 6);
  }, [allClients, data.clientName, data.selectedClient]);

  // Drag and drop sensors with touch support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms hold to start dragging
        tolerance: 5, // Allow 5px movement while holding
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // A conta do documento vem da fonte única (src/lib/quoteTotals.ts) — o mesmo
  // módulo que o PDF, a página pública e todos os saves usam. Era aqui que
  // morava uma das três cópias da fórmula.
  const totals = computeQuoteTotals(data);
  const total = totals.total;

  // Frete e desconto de cabeçalho ficam atrás de um toggle: um orçamento simples
  // (a maioria) continua com o Card Total idêntico ao de sempre, e quem precisa
  // dos campos os revela. DERIVADO, não estado espelhado: um documento que já
  // tem valores abre com os campos à mostra sem precisar de efeito de sincronia
  // (o orçamento salvo chega DEPOIS da montagem, via fetch).
  const [extrasOpenedByUser, setExtrasOpenedByUser] = useState(false);
  const showTotalsExtras =
    extrasOpenedByUser || Number(data.shipping) > 0 || Number(data.discount) > 0;

  // Count photos in items
  const photoCount = useMemo(() => {
    return data.items.filter(item => item.image_url).length;
  }, [data.items]);

  const canUploadMorePhotos = isPremium || photoCount < FREE_PHOTO_LIMIT;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  /* Cadastro rápido do cliente: mesmas validações do /clients/new. */
  const docKind = detectDocumentKind(data.newClientDocument || '');
  const docValid = isValidDocument(data.newClientDocument || '');
  const [cepLoading, setCepLoading] = useState(false);

  /** Busca de endereço pelo CEP — o MESMO helper (`lookupCep`) do /clients/new. */
  const handleCepLookup = async (rawCep: string) => {
    const digits = onlyDigits(rawCep);
    if (digits.length !== 8 || !isOnline) return;
    setCepLoading(true);
    try {
      const addr = await lookupCep(digits);
      if (addr) {
        onChange((prev) => ({
          ...prev,
          newClientLogradouro: addr.logradouro || prev.newClientLogradouro,
          newClientBairro: addr.bairro || prev.newClientBairro,
          newClientMunicipio: addr.municipioNome || prev.newClientMunicipio,
          newClientUf: addr.uf || prev.newClientUf,
        }));
      } else {
        toast.warning('CEP não encontrado.');
      }
    } finally {
      setCepLoading(false);
    }
  };

  // Só faz sentido gravar na agenda um telefone completo (DDD + número).
  const podeSalvarNaAgenda =
    Capacitor.isNativePlatform() && onlyDigits(data.newClientPhone || '').length >= 11;

  const handleSaveToContacts = async () => {
    try {
      const { Contacts } = await import('@capacitor-community/contacts');
      // NÃO checar requestPermissions() antes: o próprio createContact() pede a
      // permissão e consulta o estado VIVO. O status agregado podia vir velho e
      // fazer o app mostrar um "sem permissão" falso logo após o usuário conceder.
      await Contacts.createContact({
        contact: {
          name: { given: data.clientName || 'Novo Cliente' },
          phones: [{ type: 'Mobile' as never, number: onlyDigits(data.newClientPhone || '') }],
        },
      });
      toast.success('Contato salvo na agenda!');
    } catch (error) {
      console.error('Error creating contact:', error);
      if (/permission/i.test((error as Error)?.message || '')) {
        toast.warning('Permissão de contatos negada. Vá em Configurações > Apps > Orçamigo > Permissões.');
      } else {
        toast.error('Erro ao salvar contato. Tente novamente.');
      }
    }
  };

  const updateField = <K extends keyof QuoteFormData>(field: K, value: QuoteFormData[K]) => {
    onChange((prev) => ({ ...prev, [field]: value }));
  };

  const updateItems = (updater: (prevItems: QuoteItem[]) => QuoteItem[]) => {
    onChange((prev) => ({ ...prev, items: updater(prev.items) }));
  };

  /**
   * Liga/desliga uma forma aceita.
   *
   * O `?? []` aqui é o único lugar onde `null` vira `[]` — e é o correto: a
   * partir do momento em que o usuário toca no bloco, a seleção passa a ser
   * uma escolha EXPLÍCITA. Desmarcar tudo grava `[]` e esconde o QR PIX; é
   * justamente a diferença que `null` (legado) precisa preservar.
   * A ordem canônica é imposta por `normalizePaymentMethods`, para o PDF não
   * mudar conforme a ordem de clique.
   */
  const togglePaymentMethod = (method: PaymentMethod) => {
    onChange((prev) => {
      const current = prev.paymentMethods ?? [];
      const next = current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method];
      return { ...prev, paymentMethods: normalizePaymentMethods(next) ?? [] };
    });
  };

  /**
   * Aceita PIX mas a empresa não tem chave: dá para listar a forma, não dá para
   * gerar o QR. O aviso exige uma empresa ATIVA de propósito — o visitante não
   * tem empresa nenhuma, e mandá-lo "cadastrar a chave nas configurações da
   * empresa" seria apontar para uma tela que ele não pode abrir.
   */
  const pixSelectedWithoutKey =
    !!activeCompany && !!data.paymentMethods?.includes('pix') && !activeCompany.pix_key;

  const { scan: scanBarcode, isAvailable: barcodeAvailable, isScanning: isBarcodeScanning } = useBarcodeScanner();
  const showBarcodeButton = barcodeAvailable || import.meta.env.DEV;

  const addItemFromScannedCode = (code: string, match?: Product) => {
    if (match) {
      const newItem: QuoteItem = {
        id: createUuid(),
        name: match.name,
        description: match.description || '',
        quantity: 1,
        unit_price: match.price,
        discount: 0,
        source: 'catalog' as const,
        itemType: (match as any).type === 'product' ? 'product' as const : 'service' as const,
        unit: match.unit || 'un',
        image_url: (match as any).image_url || undefined,
        product_id: match.id,
        barcode: (match as any).barcode || code,
        // Snapshot do custo NO MOMENTO em que o item entra no orçamento.
        unit_cost: productUnitCost(match),
      };
      updateItems((prevItems) => [...prevItems.filter((item) => item.name.trim()), newItem]);
      toast.success(`Adicionado: ${match.name}`);
    } else {
      // Sem correspondência no catálogo: o código vai para o campo `barcode`
      // (não mais para o nome/descrição). O usuário completa nome e preço.
      const newItem: QuoteItem = {
        id: createUuid(),
        name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        discount: 0,
        source: 'manual' as const,
        itemType: 'product' as const,
        unit: 'un',
        barcode: code,
      };
      updateItems((prevItems) => [...prevItems.filter((item) => item.name.trim()), newItem]);
      toast.info('Código lido. Complete o nome e o preço do item.');
    }
  };

  const handleScanQuoteItem = async () => {
    const code = await scanBarcode();
    if (!code) return;
    if (!isEan13(code) && !/^\d{8,14}$/.test(code)) {
      addItemFromScannedCode(code);
      return;
    }
    let match: Product | undefined;
    if (user) {
      const cached = getCachedCatalog(user.id) || [];
      match = cached.find((p: any) => {
        if (!p) return false;
        // Match primário pelo campo dedicado de código de barras.
        if (p.barcode && p.barcode === code) return true;
        // Fallback legado: produtos antigos que guardavam o código na descrição.
        if (typeof p.description === 'string' && p.description.includes(code)) return true;
        return false;
      }) as Product | undefined;
    }
    addItemFromScannedCode(code, match);
  };

  const addManualItem = (itemType: 'service' | 'product' = 'service') => {
    updateItems((prevItems) => [
      ...prevItems,
      {
        id: createUuid(),
        name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        discount: 0,
        source: 'manual' as const,
        itemType,
      },
    ]);
  };

  /** Lista estável para o JSX — `undefined` no formulário legado vira `[]`. */
  const extraFields: QuoteExtraField[] = data.extraFields ?? [];

  const addFromCatalog = async (products: Product[]) => {
    // O catálogo é lido do cache AGORA, não guardado em estado na montagem: o
    // `CatalogSelector` acabou de buscar e cachear a lista antes de chamar este
    // callback, então um estado carregado no mount estaria vazio no primeiro
    // uso depois do login — e o kit simplesmente não expandiria, sem erro.
    const catalogProducts = (user ? getCachedCatalog(user.id) : null) ?? products;
    const newItems: QuoteItem[] = [];
    // Quantos materiais entraram por causa de um kit — o usuário precisa saber
    // que linhas apareceram sozinhas, senão parece bug.
    let bundledCount = 0;

    // Fetch product photos for all selected products in one query
    const productIds = products.map(p => p.id).filter(Boolean);
    const photosMap: Record<string, string[]> = {};
    if (productIds.length > 0 && isOnline) {
      try {
        const { data: photosData } = await supabase
          .from('product_photos')
          .select('product_id, image_url, position')
          .in('product_id', productIds)
          .order('position');
        if (photosData) {
          photosData.forEach(p => {
            if (!photosMap[p.product_id]) photosMap[p.product_id] = [];
            photosMap[p.product_id][p.position] = p.image_url;
          });
        }
      } catch (err) {
        console.error('Error fetching product photos:', err);
      }
    }

    for (const product of products) {
      const itemPhotos = photosMap[product.id] || [];
      newItems.push({
        id: createUuid(),
        name: product.name,
        description: product.description || '',
        quantity: 1,
        unit_price: product.price,
        discount: 0,
        source: 'catalog' as const,
        // Import unit and itemType from catalog
        itemType: (product as any).type === 'product' ? 'product' : 'service',
        unit: product.unit || 'un',
        // Import image_url, image_url_2, image_url_3 from catalog if they exist
        image_url: itemPhotos[0] || (product as any).image_url || undefined,
        image_url_2: itemPhotos[1] || undefined,
        image_url_3: itemPhotos[2] || undefined,
        // Store product_id to identify catalog items when reloading
        product_id: product.id,
        // Herdar o código de barras do produto do catálogo
        barcode: (product as any).barcode || undefined,
        // Snapshot do custo NO MOMENTO em que o item entra no orçamento — nunca
        // relido do catálogo depois, senão o histórico mudaria sozinho.
        unit_cost: productUnitCost(product),
        // Herdar outros metadados do catálogo
        category: product.category || null,
        internal_code: product.internal_code || null,
        track_stock: product.track_stock || false,
        min_stock: product.min_stock || null,
        lc116_item: product.lc116_item || null,
        iss_aliquota: product.iss_aliquota || null,
        iss_retido: product.iss_retido === true,
        nbs: product.nbs || null,
        cclasstrib: product.cclasstrib || null,
      });

      // MATERIAIS INCLUSOS: o serviço-kit traz os materiais como itens PRÓPRIOS,
      // cada um com o seu `product_id`. É isso que faz a baixa de estoque
      // funcionar de graça — `toDeductionLines` consolida por product_id, então
      // o mesmo material vindo de dois serviços vira UMA baixa somada.
      const bundle = bundlesByParent.get(String(product.id));
      if (!bundle || bundle.length === 0) continue;

      const resolved = resolveBundleItems(bundle, catalogProducts);
      const existingIds = new Set(newItems.map((it) => String(it.product_id ?? '')));
      for (const line of bundleLinesForQuote(resolved, { parentId: String(product.id) })) {
        // O material já foi escolhido à mão nesta mesma seleção? Não duplica a
        // linha — a quantidade que o usuário digitou vale mais.
        if (existingIds.has(String(line.product_id))) continue;
        // `bundleParentId` fica fora do item do formulário de propósito: a
        // linha materializada é um item comum a partir daqui (o usuário pode
        // editar a quantidade ou remover), e um campo extra no estado só
        // confundiria quem for ler o payload depois.
        const { bundleParentId: _origin, ...itemFields } = line;
        newItems.push({ id: createUuid(), ...itemFields });
        bundledCount += 1;
      }
    }

    updateItems((prevItems) => [...prevItems.filter((item) => item.name.trim()), ...newItems]);

    if (bundledCount > 0) {
      toast.info(
        bundledCount === 1
          ? '1 material incluso foi adicionado ao orçamento.'
          : `${bundledCount} materiais inclusos foram adicionados ao orçamento.`,
      );
    }
  };

  const removeItem = (id: string) => {
    updateItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const updateItem = (
    id: string,
    fieldOrUpdates: keyof QuoteItem | Partial<QuoteItem>,
    value?: string | number | boolean | undefined
  ) => {
    updateItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id !== id) return item;
        if (typeof fieldOrUpdates === 'object') {
          return { ...item, ...fieldOrUpdates };
        }
        return { ...item, [fieldOrUpdates]: value };
      })
    );
  };

  const { uploadFile, isUploading: isUploadingPhoto } = usePhotoUpload();

  /**
   * ─── QUAIS SLOTS DE FOTO ESTÃO SUBINDO AGORA ────────────────────────────
   *
   * O upload para o Storage leva alguns segundos numa rede de celular, e até
   * 2026-08-08 NADA na tela dizia isso: o usuário escolhia a foto, o slot
   * continuava com o ícone de câmera, e a conclusão natural era que o app tinha
   * engolido a foto. Alguns tentavam de novo, enviando duas vezes.
   *
   * O `isUploading` do `usePhotoUpload` já existia — e estava sendo
   * DESESTRUTURADO AQUI E NUNCA USADO. Ele também não bastaria: é um booleano do
   * hook inteiro, então com dois envios ao mesmo tempo os TRÊS slots de TODOS os
   * itens girariam juntos. O estado é por slot (`${itemId}:${slot}`), que é a
   * granularidade em que o usuário enxerga o que está acontecendo.
   */
  const [uploadingSlots, setUploadingSlots] = useState<ReadonlySet<string>>(new Set());

  const markSlotUploading = (key: string, uploading: boolean) => {
    setUploadingSlots((prev) => {
      const next = new Set(prev);
      if (uploading) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleImageUpload = async (id: string, file: File, slot: 'image_url' | 'image_url_2' | 'image_url_3') => {
    // Double-check PRO status before allowing upload
    if (!isPremium && slot !== 'image_url') {
      setShowProModal(true);
      return;
    }
    
    if (!canUploadMorePhotos) {
      toast.warning(`Limite de ${FREE_PHOTO_LIMIT} fotos atingido. Faça upgrade para Pro para fotos ilimitadas.`);
      return;
    }

    // If offline, store as data URL for later sync
    if (!isOnline) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        updateItem(id, slot, dataUrl);
        toast.success('Foto salva localmente! Será sincronizada quando houver internet.', {
          icon: '📴',
        });
      };
      reader.onerror = () => {
        toast.error('Erro ao salvar foto localmente.');
      };
      reader.readAsDataURL(file);
      return;
    }

    // Upload to Supabase Storage for persistence
    const key = `${id}:${slot}`;
    markSlotUploading(key, true);
    try {
      const result = await uploadFile(file, 'quote-photos', 'items');
      if (result) {
        // A foto ANTERIOR deste slot, se tiver sido subida por aqui, fica sem
        // dono no mesmo instante em que a nova entra: trocar de foto é o jeito
        // mais fácil de gerar um órfão sem ninguém perceber.
        discardUploadIfOurs(itemPhotoUrl(id, slot));
        onChange((prev) => ({
          ...prev,
          pendingUploads: trackPendingUpload(prev.pendingUploads, result.url),
        }));
        updateItem(id, slot, result.url);
        toast.success('Foto adicionada com sucesso!');
      } else {
        toast.error('Erro ao fazer upload da foto.');
      }
    } finally {
      // `finally`: se o upload estourar, o slot precisa voltar ao estado normal
      // — um spinner eterno é pior do que a ausência de spinner, porque ele
      // afirma que algo ainda está acontecendo.
      markSlotUploading(key, false);
    }
  };
  
  // Callback when non-PRO user tries to use photo feature
  const handleProFeatureClick = () => {
    setShowProModal(true);
  };

  /** A URL que este slot carrega AGORA (antes da troca/remoção). */
  const itemPhotoUrl = (id: string, slot: 'image_url' | 'image_url_2' | 'image_url_3') =>
    data.items.find((item) => item.id === id)?.[slot] ?? null;

  /**
   * Apaga a foto do Storage **se** tiver sido este formulário que a subiu.
   *
   * O `if` é a regra inteira: a mesma ação sobre um item vindo do CATÁLOGO
   * chegaria na foto do produto, que pertence ao cadastro e continua valendo
   * depois que o documento morre.
   */
  const discardUploadIfOurs = (url: string | null | undefined) => {
    if (!url) return;
    const { discard } = splitPendingUploads(data.pendingUploads, []);
    const alvo = discard.filter((entry) => entry.url === url);
    if (alvo.length === 0) return;
    void deletePendingUploads(alvo);
    onChange((prev) => ({
      ...prev,
      pendingUploads: (prev.pendingUploads ?? []).filter((entry) => entry.url !== url),
    }));
  };

  const handleImageRemove = (id: string, slot: 'image_url' | 'image_url_2' | 'image_url_3') => {
    discardUploadIfOurs(itemPhotoUrl(id, slot));
    updateItem(id, slot, undefined);
    toast.info('Foto removida.');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      updateItems((prevItems) => {
        const oldIndex = prevItems.findIndex((item) => item.id === active.id);
        const newIndex = prevItems.findIndex((item) => item.id === over.id);
        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  /**
   * Reordenar por SETAS — o caminho do TOQUE (a alça de arrasto só aparece com
   * mouse). Mesma operação do arrasto, então as duas formas produzem exatamente
   * a mesma ordem.
   */
  const moveItem = (index: number, direction: -1 | 1) => {
    updateItems((prevItems) => {
      const to = index + direction;
      return to < 0 || to >= prevItems.length ? prevItems : arrayMove(prevItems, index, to);
    });
  };

  const handleClientSelect = (client: Client) => {
    onChange((prev) => ({
      ...prev,
      selectedClient: client,
      clientName: client.name,
      // Also update phone/address/document from client
      newClientPhone: client.phone || prev.newClientPhone,
      newClientAddress: client.address || prev.newClientAddress,
      newClientDocument: client.document || prev.newClientDocument,
    }));
  };

  // Handle opening client selector - for existing quotes, show confirmation first
  const handleOpenClientSelector = () => {
    if (isExistingQuote && data.selectedClient && onClientChangeRequest) {
      // Existing quote with a client - needs confirmation
      onClientChangeRequest(() => {
        setShowClientSelector(true);
      });
    } else {
      setShowClientSelector(true);
    }
  };

  const openWhatsApp = () => {
    if (data.selectedClient?.phone) {
      const phone = data.selectedClient.phone.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  return (
    <>
      {/* No DESKTOP o formulário vira duas colunas, com o TOTAL fixo à direita.
          É a mesma razão do balcão do PDV: o prestador monta os itens e precisa
          ver o valor mudando, e empilhado ele tem de rolar até o fim do
          documento a cada linha acrescentada.

          A posição é dada por `col-start`/`row-start` nos filhos, e NÃO
          movendo o bloco do total no JSX. Movê-lo mudaria a ordem no celular,
          onde ele tem de vir logo depois dos itens — e a ordem do DOM também é
          a ordem de leitura de quem usa leitor de tela.

          `lg:space-y-0` porque no desktop quem separa é o `gap`; deixar os dois
          somaria a distância entre as linhas da coluna esquerda. */}
      <div className="space-y-5 lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-5 lg:space-y-0">
        {/* ================================================================ */}
        {/* CLIENTE — cartão de entrada do documento                          */}
        {/* ================================================================ */}
        {/* `data-tour`: âncora do 1º coach-mark do tour de boas-vindas
            (ver src/lib/welcomeTour.ts). */}
        <div data-tour="tour-quote-client" className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm lg:col-start-1">
          {user ? (
            <>
              {data.selectedClient ? (
                /* Sem avatar: cliente não tem foto neste app, e o círculo custava
                   56px do NOME — medido, "Condomínio Vila Nova" (143px) não cabia
                   nos 106px que sobravam. Sem ele, o nome fica com ~174px. */
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{data.selectedClient.name}</p>
                    {data.selectedClient.phone && (
                      <p className="truncate text-xs text-muted-foreground">
                        {data.selectedClient.phone}
                      </p>
                    )}
                  </div>
                  {/* Só o símbolo. Botão de ícone não tem nome acessível — daí o
                      `aria-label` + `title` (CLAUDE.md #142). */}
                  {data.selectedClient.phone && (
                    <Button3D
                      color="whatsapp"
                      size="sm"
                      className="h-8 w-8 shrink-0 px-0"
                      onClick={openWhatsApp}
                      aria-label="Abrir WhatsApp do cliente"
                      title="Abrir WhatsApp"
                    >
                      <WhatsAppIcon className="h-4 w-4" />
                    </Button3D>
                  )}
                  <Button3D
                    color="gray"
                    size="sm"
                    className="h-8 w-8 shrink-0 px-0"
                    onClick={handleOpenClientSelector}
                    disabled={disabled}
                    aria-label="Trocar cliente"
                    title="Trocar cliente"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button3D>
                  <Button3D
                    color="red"
                    size="sm"
                    className="h-8 w-8 shrink-0 px-0"
                    onClick={() => {
                      onChange((prev) => ({ ...prev, selectedClient: null, clientName: '' }));
                    }}
                    disabled={disabled}
                    aria-label="Limpar cliente"
                    title="Remover cliente do documento"
                  >
                    <X className="h-4 w-4" />
                  </Button3D>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Nome do cliente *"
                      value={data.clientName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        onChange((prev) => {
                          const updates: Partial<QuoteFormData> = { clientName: newName };
                          if (prev.selectedClient && newName !== prev.selectedClient.name) {
                            updates.selectedClient = null;
                          }
                          return { ...prev, ...updates };
                        });
                      }}
                      onFocus={() => setClientInputFocused(true)}
                      // Atraso para o toque na sugestão registrar antes de o menu sumir.
                      onBlur={() => setTimeout(() => setClientInputFocused(false), 150)}
                      disabled={disabled}
                      className="h-12 rounded-xl bg-background pl-10"
                      autoComplete="off"
                    />

                    {clientInputFocused && clientSuggestions.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-xl border bg-popover shadow-md">
                        {clientSuggestions.map((client) => (
                          <button
                            key={client.id}
                            type="button"
                            // onMouseDown vem antes do onBlur do input, então a
                            // escolha é aplicada antes de o menu sumir.
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleClientSelect(client);
                              setClientInputFocused(false);
                            }}
                            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
                          >
                            <span className="flex items-center gap-2 text-sm font-medium">
                              <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              {client.name}
                            </span>
                            {(client.phone || client.address) && (
                              <span className="flex flex-wrap gap-x-3 gap-y-0.5 pl-5 text-xs text-muted-foreground">
                                {client.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {client.phone}
                                  </span>
                                )}
                                {client.address && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {client.address}
                                  </span>
                                )}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2 h-10 w-full gap-2 rounded-xl border-primary/40 bg-primary/5 text-sm font-semibold text-primary hover:bg-primary/10 hover:text-primary"
                    onClick={() => setShowClientSelector(true)}
                    disabled={disabled}
                  >
                    <User className="h-4 w-4" />
                    Escolher dos meus clientes
                  </Button>
                </>
              )}

              {/* Cadastro rápido — os MESMOS campos de /clients/new, e eles são
                  gravados de verdade (buildNewClientColumns) quando o usuário
                  marca "salvar na minha lista". */}
              {data.clientName.trim() && !data.selectedClient && (
                <div className="mt-3 space-y-3 border-t border-dashed pt-3 duration-300 animate-in fade-in slide-in-from-top-2">
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <UserPlus className="h-3.5 w-3.5" />
                    Dados adicionais do cliente (opcional)
                  </p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <FieldLabel icon={FileText}>CPF / CNPJ</FieldLabel>
                      <Input
                        inputMode="numeric"
                        placeholder="CPF ou CNPJ"
                        value={formatDocument(data.newClientDocument || '')}
                        onChange={(e) =>
                          onChange((prev) => ({ ...prev, newClientDocument: onlyDigits(e.target.value) }))
                        }
                        disabled={disabled}
                        className={cn(FIELD_INPUT, docValid === false && 'border-destructive')}
                      />
                      {docKind && docValid !== false && (
                        <p className="text-[11px] text-muted-foreground">
                          {docKind === 'cpf' ? 'CPF' : 'CNPJ'} detectado.
                        </p>
                      )}
                      {docValid === false && (
                        <p className="text-[11px] text-destructive">Dígitos não conferem.</p>
                      )}
                    </div>

                    {/* "Agendar" mora DENTRO do campo: ele só existe com o telefone
                        completo, então o texto nunca chega perto da borda direita. */}
                    <div className="space-y-1.5">
                      <FieldLabel icon={Phone}>WhatsApp / telefone</FieldLabel>
                      <div className="relative">
                        <Input
                          placeholder="(00) 00000-0000"
                          value={data.newClientPhone || ''}
                          onChange={(e) =>
                            onChange((prev) => ({ ...prev, newClientPhone: e.target.value }))
                          }
                          disabled={disabled}
                          className={cn(
                            FIELD_INPUT,
                            podeSalvarNaAgenda && 'pr-[96px]',
                          )}
                        />
                        {podeSalvarNaAgenda && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 h-8 -translate-y-1/2 gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2 text-[11px] font-semibold text-primary hover:bg-primary/10 hover:text-primary"
                            onClick={handleSaveToContacts}
                          >
                            <Contact className="h-3.5 w-3.5" />
                            Agendar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <FieldLabel>E-mail</FieldLabel>
                    <Input
                      type="email"
                      placeholder="cliente@email.com"
                      value={data.newClientEmail || ''}
                      onChange={(e) => onChange((prev) => ({ ...prev, newClientEmail: e.target.value }))}
                      disabled={disabled}
                      className={FIELD_INPUT}
                    />
                  </div>

                  <div className="space-y-3 rounded-xl border p-3">
                    <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      Endereço (para nota fiscal)
                    </p>

                    <div className="grid grid-cols-3 items-start gap-3">
                      <div className="space-y-1.5">
                        <FieldLabel>CEP</FieldLabel>
                        <div className="relative">
                          <Input
                            inputMode="numeric"
                            placeholder="00000-000"
                            value={formatCep(data.newClientCep || '')}
                            onChange={(e) =>
                              onChange((prev) => ({ ...prev, newClientCep: onlyDigits(e.target.value) }))
                            }
                            onBlur={(e) => handleCepLookup(e.target.value)}
                            disabled={disabled}
                            className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                          />
                          {cepLoading && (
                            <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 space-y-1.5">
                        <FieldLabel>Logradouro</FieldLabel>
                        <Input
                          placeholder="Rua, avenida..."
                          value={data.newClientLogradouro || ''}
                          onChange={(e) =>
                            onChange((prev) => ({ ...prev, newClientLogradouro: e.target.value }))
                          }
                          disabled={disabled}
                          className={FIELD_INPUT}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 items-start gap-3">
                      <div className="space-y-1.5">
                        <FieldLabel>Número</FieldLabel>
                        <Input
                          placeholder="123"
                          value={data.newClientNumero || ''}
                          onChange={(e) =>
                            onChange((prev) => ({ ...prev, newClientNumero: e.target.value }))
                          }
                          disabled={disabled}
                          className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                        />
                      </div>
                      <div className="col-span-2 space-y-1.5">
                        <FieldLabel>Complemento</FieldLabel>
                        <Input
                          placeholder="Apto, sala, bloco..."
                          value={data.newClientComplemento || ''}
                          onChange={(e) =>
                            onChange((prev) => ({ ...prev, newClientComplemento: e.target.value }))
                          }
                          disabled={disabled}
                          className={FIELD_INPUT}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel>Bairro</FieldLabel>
                      <Input
                        placeholder="Bairro / distrito"
                        value={data.newClientBairro || ''}
                        onChange={(e) =>
                          onChange((prev) => ({ ...prev, newClientBairro: e.target.value }))
                        }
                        disabled={disabled}
                        className={FIELD_INPUT}
                      />
                    </div>

                    <div className="grid grid-cols-3 items-start gap-3">
                      <div className="col-span-2 space-y-1.5">
                        <FieldLabel>Município</FieldLabel>
                        <Input
                          placeholder="Cidade"
                          value={data.newClientMunicipio || ''}
                          onChange={(e) =>
                            onChange((prev) => ({ ...prev, newClientMunicipio: e.target.value }))
                          }
                          disabled={disabled}
                          className={FIELD_INPUT}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel>UF</FieldLabel>
                        <Input
                          maxLength={2}
                          placeholder="UF"
                          value={data.newClientUf || ''}
                          onChange={(e) =>
                            onChange((prev) => ({
                              ...prev,
                              newClientUf: e.target.value.toUpperCase().replace(/[^A-Z]/g, ''),
                            }))
                          }
                          disabled={disabled}
                          className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel>Inscrição estadual</FieldLabel>
                      <Input
                        placeholder="ISENTO para consumidor final"
                        value={data.newClientIe || ''}
                        onChange={(e) => onChange((prev) => ({ ...prev, newClientIe: e.target.value }))}
                        disabled={disabled}
                        className={FIELD_INPUT}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="save-client"
                      checked={data.saveNewClient || false}
                      onCheckedChange={(checked) =>
                        onChange((prev) => ({ ...prev, saveNewClient: checked === true }))
                      }
                      onClick={(e) => e.stopPropagation()}
                      disabled={disabled}
                    />
                    <div
                      className="cursor-pointer text-sm leading-tight"
                      onClick={() => !disabled && onChange((prev) => ({ ...prev, saveNewClient: !prev.saveNewClient }))}
                    >
                      Salvar este cliente na minha lista
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Nome do cliente *"
                value={data.clientName}
                onChange={(e) => updateField('clientName', e.target.value)}
                disabled={disabled}
                className="h-12 rounded-xl bg-background pl-10"
              />
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* TÍTULO — o nome que o prestador dá ao documento                   */}
        {/* ================================================================ */}
        {/* Fica VISÍVEL, logo abaixo do cliente, e não dentro do accordion de
            detalhes: ele nasceu lá embaixo, recolhido e depois do Total, e o
            Radix DESMONTA conteúdo fechado — então, na prática, quem não sabia
            que o campo existia nunca o encontrava. É o dado que distingue duas
            linhas na lista de `/quotes` (sem ele o card cai no nome do cliente,
            e dois orçamentos do mesmo cliente ficam indistinguíveis, #175). */}
        <div className="space-y-1.5 rounded-2xl border border-border/60 bg-card p-4 shadow-sm lg:col-start-1">
          <FieldLabel icon={Pencil} htmlFor="quote-title">
            Título {getDocumentTypeLabel(data.documentType).preposition} (opcional)
          </FieldLabel>
          <Input
            id="quote-title"
            placeholder="Ex: Reforma da cozinha"
            value={data.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            disabled={disabled}
            className={FIELD_INPUT}
          />
          <p className="text-xs text-muted-foreground">
            Sem título, o documento aparece na lista pelo nome do cliente.
          </p>

          {/* SUBTÍTULO — mora no MESMO cartão do título de propósito: os dois
              formam o cabeçalho do documento, e separá-los faria o usuário
              preencher um sem ver o outro. Como o título SUBSTITUI a palavra do
              tipo no PDF (#202), é o subtítulo que devolve a informação do que
              o serviço é. Vazio = o PDF sai exatamente como sempre saiu. */}
          <div className="space-y-1.5 pt-1">
            <FieldLabel icon={Pencil} htmlFor="quote-subtitle">
              Subtítulo (opcional)
            </FieldLabel>
            <Input
              id="quote-subtitle"
              placeholder="Ex: Manutenção de celulares"
              value={data.subtitle || ''}
              onChange={(e) => updateField('subtitle', e.target.value)}
              disabled={disabled}
              className={FIELD_INPUT}
            />
            <p className="text-xs text-muted-foreground">
              Aparece como uma linha menor logo abaixo do título, no PDF.
            </p>
          </div>
        </div>

        {/* ================================================================ */}
        {/* LADRILHOS — como um item entra no documento                       */}
        {/* ================================================================ */}
        {/* Atalhos + ramificação + lista: um bloco SÓ, para o `space-y` do
            formulário não separar o traço dos elementos que ele liga. */}
        <div className="lg:col-start-1">
          {!disabled && (
            <>
              {/* `data-tour`: âncora do 2º coach-mark do tour de boas-vindas. */}
              <div data-tour="tour-quote-items" className="grid grid-cols-4 gap-2">
              {[
                ...(user
                  ? [{
                      icon: Package,
                      label: 'Catálogo',
                      onClick: () => setShowCatalogSelector(true),
                      tint: 'text-primary bg-primary/10',
                      disabled: false,
                    }]
                  : []),
                ...(showBarcodeButton
                  ? [{
                      icon: Barcode,
                      label: 'Código',
                      onClick: handleScanQuoteItem,
                      tint: 'text-slate-600 bg-slate-500/10',
                      disabled: isBarcodeScanning,
                    }]
                  : []),
                {
                  icon: Wrench,
                  label: 'Serviço',
                  onClick: () => addManualItem('service'),
                  tint: 'text-[#2f6fae] bg-[#2f6fae]/10',
                  disabled: false,
                },
                {
                  icon: ShoppingBag,
                  label: 'Produto',
                  onClick: () => addManualItem('product'),
                  tint: 'text-amber-600 bg-amber-500/10',
                  disabled: false,
                },
              ].map((tile) => (
                <button
                  key={tile.label}
                  type="button"
                  onClick={tile.onClick}
                  disabled={tile.disabled}
                  className="flex flex-col items-center gap-1.5 rounded-2xl border border-border/60 bg-card px-1 py-3 transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] disabled:opacity-60"
                >
                  <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', tile.tint)}>
                    <tile.icon className="h-4 w-4" />
                  </span>
                  <span className="text-[11px] font-medium text-foreground">{tile.label}</span>
                </button>
              ))}
            </div>

            {/* RAMIFICAÇÃO — desenhada com bordas (não SVG) para acompanhar a
                largura da tela sem distorcer o traço. É ela que torna explícito
                que os atalhos ALIMENTAM a lista abaixo. */}
              <div aria-hidden className="pointer-events-none select-none">
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="mx-auto h-3 w-px bg-primary/30" />
                ))}
              </div>
              {/* cada célula avança meia sarjeta (-mx-1 de um gap-2) para o traço
                  atravessar os vãos sem emenda */}
              <div className="grid grid-cols-4 gap-2">
                <span className="-mr-1 ml-[50%] block border-t border-primary/30" />
                <span className="-mx-1 block border-t border-primary/30" />
                <span className="-mx-1 block border-t border-primary/30" />
                <span className="-ml-1 mr-[50%] block border-t border-primary/30" />
              </div>
              <div className="mx-auto h-4 w-px bg-primary/30" />
            </div>
            </>
          )}

          {/* ITENS */}
          <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4 text-primary" />
              Itens {getDocumentTypeLabel(data.documentType).itemsPreposition}
            </span>
            {!isPremium && photoCount > 0 && (
              <span className="text-[11px] text-muted-foreground">
                Fotos: {photoCount}/{FREE_PHOTO_LIMIT}
              </span>
            )}
          </div>

          {data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 px-4 py-8 text-center">
              <Package className="mb-2 h-9 w-9 text-primary/40" />
              <p className="text-sm text-muted-foreground">
                Nenhum item adicionado.
                <br />
                Use os atalhos acima para começar.
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={data.items.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {data.items.map((item, index) => (
                    <SortableQuoteItemCard
                      key={item.id}
                      item={item}
                      onUpdate={updateItem}
                      onRemove={removeItem}
                      onImageUpload={handleImageUpload}
                      onImageRemove={handleImageRemove}
                      uploadingSlots={uploadingSlots}
                      // A linha materializada de uma DESPESA COBRÁVEL é
                      // somente leitura: quem manda no valor dela é o
                      // lançamento do financeiro. Editável aqui, o preço da
                      // linha divergiria do valor da despesa em silêncio, e
                      // ninguém saberia qual dos dois é o certo.
                      disabled={disabled || isBillableQuoteLine(item)}
                      canRemove={!isBillableQuoteLine(item)}
                      showSaveToCatalog={showSaveToCatalog && !!user}
                      showTypeAndUnit={showTypeAndUnit || (showSaveToCatalog && !!user)}
                      canUploadPhoto={canUploadMorePhotos}
                      isProUser={isPremium}
                      onProFeatureClick={handleProFeatureClick}
                      index={index}
                      count={data.items.length}
                      onMove={(direction) => moveItem(index, direction)}
                      catalogContext={user ? catalogContext : undefined}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          </div>
        </div>

        {/* ================================================================ */}
        {/* TOTAL                                                             */}
        {/* ================================================================ */}
        {/* No desktop este é o único bloco da coluna DIREITA, e ele gruda:
            `row-start-1` o traz para o topo sem que o JSX mude de ordem. */}
        <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-800 to-emerald-950 p-4 text-white shadow-lg lg:col-start-2 lg:row-start-1 lg:sticky lg:top-6">
          {!disabled && (
            showTotalsExtras ? (
              <div className="mb-3 flex flex-wrap items-start gap-3 rounded-xl bg-white/10 p-3">
                <div className="space-y-1">
                  <Label htmlFor="quote-shipping" className="text-[11px] text-white/70">
                    Frete
                  </Label>
                  <MoneyInput
                    id="quote-shipping"
                    value={data.shipping}
                    onCommit={(value) => updateField('shipping', value)}
                    className="w-24 text-foreground"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="quote-discount" className="text-[11px] text-white/70">
                    Desconto no total
                  </Label>
                  <DiscountField
                    id="quote-discount"
                    value={data.discount}
                    type={data.discountType || 'value'}
                    onValueChange={(value) => updateField('discount', value)}
                    onTypeChange={(value) => updateField('discountType', value)}
                    className="w-[136px] text-foreground"
                  />
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-3 h-8 rounded-full bg-white/10 px-3 text-xs text-white/85 hover:bg-white/20 hover:text-white"
                onClick={() => setExtrasOpenedByUser(true)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Frete ou desconto no total
              </Button>
            )
          )}

          <div className="[&_span]:text-white/70">
            <QuoteTotalsBreakdown
              totals={totals}
              formatCurrency={formatCurrency}
              className="border-b pb-3"
            />
          </div>

          <div className="mt-3 flex items-end justify-between">
            <span className="text-sm text-white/75">Total</span>
            <span className="text-3xl font-bold">{formatCurrency(total)}</span>
          </div>
        </div>

        {/* ================================================================ */}
        {/* DETALHES DO DOCUMENTO                                             */}
        {/* ================================================================ */}
        <Accordion type="single" collapsible className="space-y-3 lg:col-start-1">
          <AccordionItem
            value="identificacao"
            className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm"
          >
            <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                <Pencil className="h-4 w-4 text-primary" />
                Datas e tipo
              </span>
            </AccordionTrigger>
            {/* O título saiu daqui e virou cartão visível acima dos itens — ele
                é identidade do documento, não detalhe opcional de rodapé. */}
            <AccordionContent className="space-y-3 pb-4">
              <div className="grid grid-cols-5 items-start gap-3">
                <div className="col-span-3 space-y-1.5">
                  <FieldLabel icon={Calendar}>Data</FieldLabel>
                  <Input
                    type="date"
                    value={data.quoteDate}
                    onChange={(e) => updateField('quoteDate', e.target.value)}
                    disabled={disabled}
                    className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <FieldLabel>Validade (dias)</FieldLabel>
                  <Input
                    type="number"
                    value={data.validityDays}
                    onChange={(e) => updateField('validityDays', e.target.value)}
                    disabled={disabled}
                    className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                  />
                </div>
              </div>

              {/* LOCAL DE EMISSÃO — fica junto da DATA porque os dois compõem
                  uma linha só no papel ("São Paulo, 9 de agosto de 2026"). É
                  detalhe do documento, não identidade, então accordion é o
                  lugar certo (a régua do #201). Vazio = o PDF não ganha linha
                  nenhuma, e todo documento já emitido sai idêntico. */}
              <div className="space-y-1.5">
                <FieldLabel icon={MapPin} htmlFor="quote-issue-place">
                  Local de emissão (opcional)
                </FieldLabel>
                <Input
                  id="quote-issue-place"
                  placeholder="Ex: São Paulo"
                  value={data.issuePlace || ''}
                  onChange={(e) => updateField('issuePlace', e.target.value)}
                  disabled={disabled}
                  className={FIELD_INPUT}
                />
                <p className="text-xs text-muted-foreground">
                  Imprime "cidade, data" acima da assinatura. Sem cidade, o
                  documento sai como sempre saiu.
                </p>
              </div>
              <div className="space-y-2 col-span-2 md:col-span-1">
                <Label className="flex h-4 items-center gap-2 text-sm font-medium">Tipo de documento</Label>
                <Select
                  value={data.documentType}
                  onValueChange={(value) => updateField('documentType', value as DocumentType)}
                  disabled={disabled}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="Selecione o tipo">
                      {data.documentType && (
                        <div className="flex items-center gap-2">
                          {(() => {
                            const option = allDocumentTypeOptions.find(o => o.value === data.documentType);
                            if (option) {
                              const Icon = option.icon;
                              return (
                                <>
                                  <Icon className="h-4 w-4" />
                                  {option.label}
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {documentTypeGroups.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel className="text-xs text-muted-foreground font-semibold px-2 py-1.5">
                          {group.label}
                        </SelectLabel>
                        {group.options.map((option) => {
                          const Icon = option.icon;
                          return (
                            <SelectItem key={option.value} value={option.value}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {option.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Numeração e status ficam aqui quando a PÁGINA não os mostra no
                  cabeçalho (é o caso da tela de edição). Sem esta condição, os
                  mesmos controles apareceriam duas vezes na tela de criação. */}
              {showIdentityFields && (
                <>
                  {data.sequenceId && (
                    <div className="flex items-center justify-between gap-2 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                          <Hash className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                          <span className="font-mono font-bold text-lg sm:text-xl text-primary leading-none">
                            {String(data.sequenceId).padStart(4, '0')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Label htmlFor="show-sequence" className="text-xs sm:text-sm text-muted-foreground cursor-pointer select-none">
                          <span className="sm:hidden">Exibir no PDF</span>
                          <span className="hidden sm:inline">Exibir no PDF/link</span>
                        </Label>
                        <Switch
                          id="show-sequence"
                          checked={data.showSequenceId || false}
                          onCheckedChange={(checked) => updateField('showSequenceId', checked)}
                          disabled={disabled}
                          className="scale-90 sm:scale-100 origin-right shrink-0"
                        />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="flex h-4 items-center gap-2 text-sm font-medium">Status</Label>
                    <Select
                      value={data.status}
                      onValueChange={(value) => updateField('status', value as QuoteStatusType)}
                      disabled={disabled}
                    >
                      <SelectTrigger className={cn(
                        "border-transparent font-medium text-white transition-colors hover:opacity-90",
                        data.status === 'pending' && 'bg-status-pending',
                        data.status === 'approved' && 'bg-status-approved',
                        data.status === 'completed' && 'bg-status-completed',
                        data.status === 'cancelled' && 'bg-status-cancelled',
                        data.status === 'rejected' && 'bg-status-cancelled',
                      )}>
                        <SelectValue>
                          {data.status === 'pending' && 'Pendente'}
                          {data.status === 'approved' && 'Aprovado'}
                          {data.status === 'completed' && 'Concluído'}
                          {data.status === 'cancelled' && 'Cancelado'}
                          {data.status === 'rejected' && 'Rejeitado'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-status-pending" />
                            Pendente
                          </div>
                        </SelectItem>
                        <SelectItem value="approved">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-status-approved" />
                            Aprovado
                          </div>
                        </SelectItem>
                        <SelectItem value="completed">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-status-completed" />
                            Concluído
                          </div>
                        </SelectItem>
                        <SelectItem value="cancelled">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-status-cancelled" />
                            Cancelado
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="pagamento"
            className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm"
          >
            <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                Pagamento e garantia
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-4">
              {/*
                GARANTIA — número + unidade, com o texto livre PRESERVADO.

                Documento antigo guarda a garantia numa frase ("1 ano na peça,
                90 dias na mão de obra") que não cabe em número+unidade. Ele
                continua editando a frase, e o app só OFERECE a conversão
                quando o texto é inequívoco; converter em massa por parsing é
                exatamente o erro da armadilha #64. Ver `lib/quoteWarranty.ts`.
              */}
              <div className="space-y-2">
                <FieldLabel>Garantia</FieldLabel>
                {data.warrantyAmount != null || !data.warranty.trim() ? (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="Ex: 90"
                      className="flex-1 bg-background"
                      value={data.warrantyAmount ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value.replace(',', '.').trim();
                        const parsed = Number(raw);
                        const amount = raw && Number.isFinite(parsed) && parsed > 0 ? parsed : null;
                        updateField('warrantyAmount', amount);
                        // Os dois andam juntos (CHECK no banco): limpar o
                        // número limpa a unidade, e informar o número escolhe
                        // "dias" se ainda não houver unidade.
                        updateField('warrantyUnit', amount == null ? null : (data.warrantyUnit ?? 'days'));
                      }}
                      disabled={disabled}
                    />
                    <Select
                      value={data.warrantyUnit ?? 'days'}
                      onValueChange={(value) => {
                        updateField('warrantyUnit', value as WarrantyUnit);
                        if (data.warrantyAmount == null) updateField('warrantyUnit', null);
                      }}
                      disabled={disabled || data.warrantyAmount == null}
                    >
                      <SelectTrigger className="w-32 bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WARRANTY_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Ex: 90 dias"
                      value={data.warranty}
                      onChange={(e) => updateField('warranty', e.target.value)}
                      disabled={disabled}
                      className="bg-background"
                    />
                    {(() => {
                      // SUGESTÃO, nunca conversão automática: só aparece para
                      // texto inequívoco, e quem converte é o clique.
                      const suggestion = suggestWarranty(data.warranty);
                      if (!suggestion || disabled) return null;
                      return (
                        <button
                          type="button"
                          className="text-xs font-medium text-primary underline"
                          onClick={() => {
                            updateField('warrantyAmount', suggestion.warrantyAmount);
                            updateField('warrantyUnit', suggestion.warrantyUnit);
                            updateField('warranty', '');
                          }}
                        >
                          Usar como{' '}
                          {formatStructuredWarranty(
                            suggestion.warrantyAmount,
                            suggestion.warrantyUnit,
                          )}{' '}
                          (permite filtrar e acompanhar o prazo)
                        </button>
                      );
                    })()}
                  </>
                )}
              </div>
              {/*
                Formas de pagamento ACEITAS (plural) — não confundir com o
                `payment_method` (singular) do PDV, que é a forma efetivamente
                usada numa venda. Os rótulos vêm de `PAYMENT_METHOD_LABELS`, o
                mesmo vocabulário do financeiro: uma segunda lista de códigos
                para a mesma coisa foi o desastre das unidades (armadilha #64).
              */}
              <div className="space-y-2">
                <FieldLabel>Formas de pagamento aceitas</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {QUOTE_PAYMENT_METHODS.map((method) => {
                    const checked = (data.paymentMethods ?? []).includes(method);
                    return (
                      <button
                        key={method}
                        id={`payment-method-${method}`}
                        type="button"
                        // Chip é um botão de ALTERNÂNCIA: quem lê tela precisa do
                        // `aria-pressed` para saber se a forma está aceita ou não.
                        aria-pressed={checked}
                        disabled={disabled}
                        onClick={() => togglePaymentMethod(method)}
                        className={cn(
                          'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60',
                          checked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input bg-background text-muted-foreground hover:bg-accent',
                        )}
                      >
                        {PAYMENT_METHOD_LABELS[method]}
                      </button>
                    );
                  })}
                </div>

                {pixSelectedWithoutKey && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Você aceita PIX, mas ainda não cadastrou a chave nas configurações da
                    empresa — o documento vai listar o PIX sem o QR Code.
                  </p>
                )}
                {data.paymentMethods?.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhuma forma selecionada: o documento não vai listar formas de
                    pagamento{activeCompany?.pix_key ? ' nem o QR Code do PIX' : ''}.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <FieldLabel>Condições de pagamento</FieldLabel>
                <Input
                  placeholder="Ex: 50% na entrada e 50% na entrega"
                  value={data.paymentTerms || ''}
                  onChange={(e) => updateField('paymentTerms', e.target.value)}
                  disabled={disabled}
                  className="bg-background"
                />
                {!disabled && (
                  <div className="flex flex-wrap gap-1.5">
                    {PAYMENT_TERMS_SUGGESTIONS.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => updateField('paymentTerms', suggestion)}
                        className="rounded-full border border-input bg-background px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <FieldLabel>Referência / nº externo</FieldLabel>
                <Input
                  placeholder="Ex: OC 4471, contrato 2026/12"
                  value={data.reference || ''}
                  onChange={(e) => updateField('reference', e.target.value)}
                  disabled={disabled}
                  className="bg-background"
                />
                <p className="text-xs text-muted-foreground">
                  Número do cliente (ordem de compra, contrato, chamado). A numeração do
                  Orçamigo continua sendo a do documento.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="textos"
            className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm"
          >
            <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-primary" />
                Observações para o cliente
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pb-4">
              <div className="space-y-2">
                <FieldLabel>Observações</FieldLabel>
                <Textarea
                  placeholder="Notas adicionais..."
                  value={data.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  disabled={disabled}
                  rows={3}
                  className="bg-background"
                />
              </div>
              {/* Terms and Conditions - Hidden for visitors (no user logged in) */}
              {user && (
                <div className="space-y-2">
                  <FieldLabel>Termos e condições / contrato</FieldLabel>
                  <Textarea
                    placeholder="Termos legais, condições de pagamento, cláusulas contratuais..."
                    value={data.termsConditions}
                    onChange={(e) => updateField('termsConditions', e.target.value)}
                    disabled={disabled}
                    rows={5}
                    className="bg-background"
                  />
                </div>
              )}

              {/* ============================================================ */}
              {/* CAMPOS EXTRAS — o prestador cria o RÓTULO (lacuna L5)         */}
              {/* ============================================================ */}
              {/* Fica junto de Observações porque é o mesmo grupo: informação
                  livre que sai no documento. A diferença é que aqui o rótulo é
                  dele — "Placa do veículo", "Centro de custo", "Nº da OS do
                  cliente" —, em vez de virar uma linha perdida nas observações. */}
              {user && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <FieldLabel>Campos extras</FieldLabel>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      disabled={disabled || extraFields.length >= MAX_EXTRA_FIELDS}
                      onClick={() =>
                        updateField('extraFields', [...extraFields, { label: '', value: '' }])
                      }
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Adicionar
                    </Button>
                  </div>

                  {extraFields.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Crie um campo com o nome que quiser — ele sai no PDF logo
                      abaixo das observações.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {extraFields.map((field, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <Input
                            aria-label={`Nome do campo extra ${index + 1}`}
                            placeholder="Ex: Placa do veículo"
                            value={field.label}
                            maxLength={MAX_EXTRA_LABEL}
                            onChange={(e) =>
                              updateField(
                                'extraFields',
                                extraFields.map((f, i) =>
                                  i === index ? { ...f, label: e.target.value } : f,
                                ),
                              )
                            }
                            disabled={disabled}
                            className={cn(FIELD_INPUT, 'min-w-0 flex-1')}
                          />
                          <Input
                            aria-label={`Valor do campo extra ${index + 1}`}
                            placeholder="Ex: ABC-1234"
                            value={field.value}
                            maxLength={MAX_EXTRA_VALUE}
                            onChange={(e) =>
                              updateField(
                                'extraFields',
                                extraFields.map((f, i) =>
                                  i === index ? { ...f, value: e.target.value } : f,
                                ),
                              )
                            }
                            disabled={disabled}
                            className={cn(FIELD_INPUT, 'min-w-0 flex-1')}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0 text-destructive"
                            disabled={disabled}
                            onClick={() =>
                              updateField(
                                'extraFields',
                                extraFields.filter((_, i) => i !== index),
                              )
                            }
                            aria-label={`Remover campo extra ${index + 1}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* O teto é VISÍVEL, nunca silencioso (#50). */}
                  {extraFields.length >= MAX_EXTRA_FIELDS && (
                    <p className="text-xs text-muted-foreground">
                      Limite de {MAX_EXTRA_FIELDS} campos extras por documento.
                    </p>
                  )}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="pdf-template"
            className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4 shadow-sm"
          >
            <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-primary" />
                Aparência do PDF
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-2 pb-4">
              <PdfTemplatePicker
                value={resolveQuotePdfTemplate(data.pdfTemplate, activeCompany?.pdf_template)}
                onChange={(tpl) => updateField('pdfTemplate', tpl)}
                accent={activeCompany?.primary_color || '#10B981'}
                disabled={disabled}
              />
              <p className="text-xs text-muted-foreground">
                Vale só para este documento. O padrão de todos os novos fica nas
                configurações da empresa.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Envolvido porque o componente não aceita `className` — e sem a
            coluna explícita ele cairia na direita, embaixo do total. */}
        <div className="lg:col-start-1">
          <GeneralPhotosSection
            photos={data.generalPhotos || []}
            onChange={(photos) => updateField('generalPhotos', photos)}
            disabled={disabled}
            isProUser={isPremium}
            onProFeatureClick={handleProFeatureClick}
          />
        </div>
      </div>

      {/* Modals */}
      <ClientSelector 
        open={showClientSelector} 
        onOpenChange={setShowClientSelector}
        onSelect={handleClientSelect}
      />
      <CatalogSelector
        open={showCatalogSelector}
        onOpenChange={setShowCatalogSelector}
        onSelect={addFromCatalog}
      />

      {/* Premium Modal for photo feature */}
      <PremiumModal
        open={showProModal}
        onOpenChange={setShowProModal}
      />
    </>
  );
}


export function useQuoteFormData(initialData?: Partial<QuoteFormData>): [QuoteFormData, React.Dispatch<React.SetStateAction<QuoteFormData>>] {
  const [formData, setFormData] = useState<QuoteFormData>({
    clientName: initialData?.clientName || '',
    selectedClient: initialData?.selectedClient || null,
    quoteDate: initialData?.quoteDate || todayIsoDate(),
    status: initialData?.status || 'pending',
    documentType: initialData?.documentType || 'orcamento',
    validityDays: initialData?.validityDays || '7',
    warranty: initialData?.warranty || '',
    warrantyAmount: initialData?.warrantyAmount ?? null,
    warrantyUnit: initialData?.warrantyUnit ?? null,
    notes: initialData?.notes || '',
    termsConditions: initialData?.termsConditions || '',
    items: initialData?.items || [],
    shipping: initialData?.shipping ?? 0,
    discount: initialData?.discount ?? 0,
    discountType: initialData?.discountType || 'value',
    // `?? null` e NÃO `?? []`: array vazio significa "não aceito nenhuma forma"
    // e esconderia o QR PIX de todo orçamento novo. Ver quotePaymentInfo.ts.
    paymentMethods: initialData?.paymentMethods ?? null,
    paymentTerms: initialData?.paymentTerms || '',
    reference: initialData?.reference || '',
    pdfTemplate: initialData?.pdfTemplate ?? null,
    newClientPhone: initialData?.newClientPhone || '',
    newClientAddress: initialData?.newClientAddress || '',
    newClientDocument: initialData?.newClientDocument || '',
    saveNewClient: initialData?.saveNewClient || false,
    generalPhotos: initialData?.generalPhotos || [],
  });

  return [formData, setFormData];
}

/**
 * Total do documento a partir do formulário — frete e desconto de cabeçalho
 * incluídos. É o valor que vai para `quotes.total`.
 *
 * A assinatura recebe o FORMULÁRIO INTEIRO de propósito. A versão anterior era
 * `calculateTotal(items)`, e uma função que só enxerga os itens não tem como
 * somar frete: os ~10 call-sites continuariam compilando e gravariam um total
 * sem frete, silenciosamente. Trocando o parâmetro, o `tsc` obriga cada um a ser
 * revisitado.
 */
export function calculateTotal(data: QuoteTotalsInput): number {
  return quoteGrandTotal(data);
}
